import React, { Component } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { reorder } from '~/helpers';
import {
	moveUp,
	moveDown,
	removeAnswerEntity,
	updateQuestionAnswers,
	updateQuestionType,
} from '~/redux/actions/workspaceActions';
import { updateAnswer } from '~/api';
import { calculatePoints } from '~/util';
import QuestionTypeClassic from '~/components/quizzes/question-type-classic';
import QuestionTypeMatrix from '~/components/quizzes/question-type-matrix';
import QuestionTypeFree from '~/components/quizzes/question-type-free';
import QuestionTypeRichtext from '~/components/quizzes/question-type-richtext';
import QuestionTypeEssay from '~/components/quizzes/question-type-essay';
import Icon from '~/components/common/icon';

/**
 * Question Child component for the workspace. It contains answers.
 */
class QuestionChildWorkspace extends Component {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		const { node } = this.props;

		/**
		 * Component state
		 */
		this.state = {
			showFormIndividual: false,
			answer: node.answer,
			correct: node.correct,
			html: node.html,
			mediaID: '',
			updated: false,
			gradedType: node.gradedType ? node.gradedType : 'text',
			gradingProgression: node.gradingProgression
				? node.gradingProgression
				: 'not-graded-none', // Default to "No grading, no points given"
			sortString: node.sortString,
			sortStringHtml: node.sortStringHtml,
			expandAll: this.props.workspace.present.expandAll,
		};

		this.handleShowFormIndividual = this.handleShowFormIndividual.bind(
			this
		);
		this.handleAnswerRemove = this.handleAnswerRemove.bind( this );
		this.handleAnswerUpdate = this.handleAnswerUpdate.bind( this );
		this.handleCheckboxChange = this.handleCheckboxChange.bind( this );
		this.handleInputChange = this.handleInputChange.bind( this );
		this.handleRichtextChange = this.handleRichtextChange.bind( this );
		this.handleAddMedia = this.handleAddMedia.bind( this );
		this.handleCorrectChange = this.handleCorrectChange.bind( this );
		this.handleMoveUp = this.handleMoveUp.bind( this );
		this.handleMoveDown = this.handleMoveDown.bind( this );

		// to prevent memory leaks
		this._isMounted = false;
	}

	/**
	 * Toggles checkbox
	 *
	 * @param {handleCheckboxChange} e
	 */
	handleCheckboxChange( e ) {
		const { target } = e;
		const { name, checked } = target;
		this.setState( { [ name ]: checked } );
	}

	/**
	 * Handles input and textarea changes
	 *
	 * @param {handleInputChange} e
	 */
	handleInputChange( e ) {
		const { target } = e;
		const { name, value } = target;
		this.setState( { [ name ]: value } );
	}

	/**
	 * Handles the change in the rich text editor
	 *
	 * @param {string} value
	 */
	handleRichtextChange( value ) {
		this.setState( { answer: value } );
	}

	/**
	 * Inserts media in answer field
	 *
	 * @param {Object} media Selected media object
	 */
	handleAddMedia( media ) {
		let mediaHTML = `${ LearnDashData.i18n.supported_media_in_answers }`;

		if ( 'image' == media.type ) {
			mediaHTML = ` <img src="${ media.url }" alt=${ media.alt } />`;
		}

		if ( 'audio' == media.type ) {
			mediaHTML = ` [audio src="${ media.url }"]`;
		}

		if ( 'video' == media.type ) {
			mediaHTML = ` [video src="${ media.url }"]`;
		}

		this.setState( ( { answer } ) => ( {
			mediaID: media.id,
			html: true,
			answer: answer + mediaHTML,
		} ) );
	}

	/**
	 * Remove answer
	 *
	 * @param {handleAnswerRemove} e
	 */
	async handleAnswerRemove( e ) {
		e.preventDefault();
		const { question, node, index, removeAnswerEntity } = this.props;

		// Create new answers array for our question type
		const newAnswers = question.answers[
			LearnDashData.questions_types_map[ question.question_type ]
		].filter( ( el ) => el != node );

		// Update question
		const res = await updateAnswer( question.ID, newAnswers );

		if ( 200 === res ) {
			// unlock question
			this.props.unlockQuestion();

			// Update our workspace
			removeAnswerEntity( index, question );
		}
	}

	/**
	 * Update the correct answer
	 *
	 * @param {handleCorrectChange} e
	 */
	async handleCorrectChange( e ) {
		const { question, node, updateQuestionAnswers } = this.props;
		const { checked } = e.target;

		const newCorrect = checked;

		// Update current answer object
		const newAnswers = question.answers[
			LearnDashData.questions_types_map[ question.question_type ]
		].map( ( answer ) => {
			if ( answer === node ) {
				return { ...answer, correct: newCorrect };
			}
			if ( 'multiple' !== question.question_type ) {
				return { ...answer, correct: false };
			}
			return answer;
		} );

		// Update question
		const res = await updateAnswer( question.ID, newAnswers );

		if ( 200 === res ) {
			// Update our workspace
			// this.setState( { correct: newCorrect } );
			updateQuestionAnswers( question, newAnswers );
		}
	}

	/**
	 * Update answer
	 *
	 * @param {handleAnswerUpdate} e
	 */
	async handleAnswerUpdate( e ) {
		e.preventDefault();
		const {
			answer,
			correct,
			html,
			gradedType,
			gradingProgression,
			sortString,
			sortStringHtml,
		} = this.state;
		const { question, index, updateQuestionType } = this.props;

		const newObject = {
			answer,
			correct,
			html,
			graded: '1',
			gradedType,
			gradingProgression,
			sortString,
			sortStringHtml,
			answerType: question.question_type,
		};

		// Update current answer object
		const newAnswer = {
			...question.answers[
				LearnDashData.questions_types_map[ question.question_type ]
			][ index ],
			...newObject,
		};

		// Re-assign current answer object
		question.answers[
			LearnDashData.questions_types_map[ question.question_type ]
		][ index ] = newAnswer;

		const newAnswers =
			question.answers[
				LearnDashData.questions_types_map[ question.question_type ]
			];

		const shouldCalculatePoints =
			'assessment_answer' === question.question_type;
		// Update question
		const res = await updateAnswer(
			question.ID,
			newAnswers,
			shouldCalculatePoints
		);

		this.props.handleShowForm();
		this.handleShowFormIndividual();

		if ( shouldCalculatePoints ) {
			const { points } = calculatePoints( newAnswers );
			updateQuestionType( { ...question, points } );
		} else {
			updateQuestionType( { ...question } );
		}

		if ( 200 === res ) {
			if ( this._isMounted ) {
				// Update our workspace
				this.setState( () => ( { updated: true } ) );
			}
		}
	}

	/**
	 * Moves answer up
	 *
	 * @param {handleMoveUp} e
	 */
	async handleMoveUp( e ) {
		e.preventDefault();
		const { question, node, index, moveUp } = this.props;

		// Create new answers array for our question type
		const newAnswers = reorder(
			question.answers[
				LearnDashData.questions_types_map[ question.question_type ]
			],
			index,
			parseInt( index - 1, 10 )
		);

		// Update our workspace
		moveUp( node, index, question );

		// Update question in the background
		await updateAnswer( question.ID, newAnswers );
	}

	/**
	 * Moves answer down
	 *
	 * @param {handleMoveDown} e
	 */
	async handleMoveDown( e ) {
		e.preventDefault();
		const { question, node, index, moveDown } = this.props;

		// Create new answers array for our question type
		const newAnswers = reorder(
			question.answers[
				LearnDashData.questions_types_map[ question.question_type ]
			],
			index,
			parseInt( index + 1, 10 )
		);

		// Update our workspace
		moveDown( node, index, question );

		// Update question in the background
		await updateAnswer( question.ID, newAnswers );
	}

	/**
	 * When component is mounted
	 */
	componentDidMount() {
		this._isMounted = true;
	}

	/**
	 * When component is unmounted
	 */
	componentWillUnmount() {
		this._isMounted = false;
	}

	/**
	 * Displays edit mode - individual items
	 *
	 * @param {handleShowFormIndividual} e
	 */
	handleShowFormIndividual( e ) {
		if ( e ) {
			e.preventDefault();
		}

		this.setState( ( { showFormIndividual } ) => ( {
			showFormIndividual: ! showFormIndividual,
		} ) );
	}

	/**
	 * Render function
	 *
	 * @event handleCheckboxChange
	 * @event handleInputChange
	 * @event handleAnswerRemove
	 * @event handleAnswerUpdate
	 * @event handleCorrectChange
	 * @event handleMoveUp
	 * @event handleMoveDown
	 * @event handleShowFormIndividual
	 */
	render() {
		const {
			showFormIndividual,
			answer,
			correct,
			html,
			sortString,
			sortStringHtml,
			gradedType,
			gradingProgression,
			updated,
		} = this.state;
		const {
			showForm,
			question,
			index,
			node,
			key,
			handleShowForm,
		} = this.props;
		const {
			question_type: questionType,
			ID: questionID,
			post_content: questionContent,
		} = question;

		if (
			'single' === questionType ||
			'multiple' === questionType ||
			'sort_answer' === questionType ||
			'matrix_sort_answer' === questionType
		) {
			return (
				<Draggable
					draggableId={ JSON.stringify( { ...node } ) }
					index={ index }
					key={ key }
				>
					{ ( provided ) => (
						<li
							key={ key }
							ref={ provided.innerRef }
							{ ...provided.draggableProps }
							className={ `ld__builder--child-item ld-question-answer ld-node-header sfwd_options ${
								showFormIndividual ? '-showform -editing' : ''
							}` }
						>
							<div className="ld-question-answer__order">
								{ /* Commented out, recommended by UX but this can be re-used later if needed
									<button className="ld-move-up" onClick={ this.handleMoveUp } aria-label="Move up"><Icon icon="chevron-up" /></button>
								*/ }
								<span className="ld-node-header__drag-handle">
									<span { ...provided.dragHandleProps }>
										<Icon icon="drag" />
									</span>
								</span>
								{ /* Commented out, recommended by UX but this can be re-used later if needed
									<button className="ld-move-down" onClick={ this.handleMoveDown } aria-label="Move down"><Icon icon="chevron-down" /></button>
								*/ }
							</div>
							{ ( 'single' === questionType ||
								'multiple' === questionType ||
								'sort_answer' === questionType ) && (
								<QuestionTypeClassic
									showFormIndividual={ showFormIndividual }
									handleAnswerUpdate={
										this.handleAnswerUpdate
									}
									handleAnswerRemove={
										this.handleAnswerRemove
									}
									handleShowForm={ ( e ) => {
										e.preventDefault();
										this.handleShowFormIndividual();
										handleShowForm();
									} }
									handleCheckboxChange={
										this.handleCheckboxChange
									}
									handleInputChange={ this.handleInputChange }
									handleAddMedia={ this.handleAddMedia }
									handleCorrectChange={
										this.handleCorrectChange
									}
									questionType={ questionType }
									questionID={ questionID }
									questionContent={ questionContent }
									index={ index }
									answer={ answer }
									correct={ node.correct }
									html={ html }
								/>
							) }
							{ 'matrix_sort_answer' === questionType && (
								<QuestionTypeMatrix
									showFormIndividual={ showFormIndividual }
									handleAnswerUpdate={
										this.handleAnswerUpdate
									}
									handleAnswerRemove={
										this.handleAnswerRemove
									}
									handleShowForm={ ( e ) => {
										e.preventDefault();
										this.handleShowFormIndividual();
										handleShowForm();
									} }
									handleCheckboxChange={
										this.handleCheckboxChange
									}
									handleInputChange={ this.handleInputChange }
									handleAddMedia={ this.handleAddMedia }
									answer={ answer }
									html={ html }
									sortString={ sortString }
									sortStringHtml={ sortStringHtml }
								/>
							) }
						</li>
					) }
				</Draggable>
			);
		} else if (
			'cloze_answer' === questionType ||
			'assessment_answer' === questionType
		) {
			return (
				<li
					key={ key }
					className={ `ld__builder--child-item ld-question-answer ld-node-header sfwd_options ${
						showForm ? '-showform -editing' : ''
					}` }
				>
					<QuestionTypeRichtext
						handleAnswerUpdate={ this.handleAnswerUpdate }
						handleRichtextChange={ this.handleRichtextChange }
						handleShowForm={ handleShowForm }
						questionType={ questionType }
						questionID={ questionID }
						answer={ answer }
						updated={ updated }
						showForm={ showForm }
					/>
				</li>
			);
		} else if ( 'essay' === questionType ) {
			return (
				<li
					key={ key }
					className={ `ld__builder--child-item ld-question-answer ld-node-header sfwd_options ${
						showForm ? '-showform -editing' : ''
					}` }
				>
					<QuestionTypeEssay
						handleAnswerUpdate={ this.handleAnswerUpdate }
						handleInputChange={ this.handleInputChange }
						handleShowForm={ handleShowForm }
						gradedType={ gradedType }
						gradingProgression={ gradingProgression }
						updated={ updated }
						showForm={ showForm }
					/>
				</li>
			);
		}
		return (
			'free_answer' === questionType && (
				<li
					key={ key }
					className={ `ld__builder--child-item ld-question-answer ld-node-header sfwd_options ${
						showForm ? '-showform -editing' : ''
					}` }
				>
					<QuestionTypeFree
						handleAnswerUpdate={ this.handleAnswerUpdate }
						handleInputChange={ this.handleInputChange }
						handleShowForm={ handleShowForm }
						answer={ answer }
						updated={ updated }
						showForm={ showForm }
					/>
				</li>
			)
		);
	}
}

/**
 * Valid props
 */
QuestionChildWorkspace.propTypes = {
	showForm: PropTypes.bool,
	node: PropTypes.object,
	question: PropTypes.object,
	index: PropTypes.number,
	moveUp: PropTypes.func,
	moveDown: PropTypes.func,
	removeAnswerEntity: PropTypes.func,
	updateQuestionAnswers: PropTypes.func,
	workspace: PropTypes.object,
	key: PropTypes.string,
	handleShowForm: PropTypes.func,
	lockQuestion: PropTypes.func,
	unlockQuestion: PropTypes.func,
	updateQuestionType: PropTypes.func,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 * @param {*} dispatch
 */
const mapDispatchToProps = ( dispatch ) => ( {
	moveUp: ( node, index, question ) =>
		dispatch(
			moveUp( {
				...node,
				index,
				parentID: question.ID,
				answerType:
					LearnDashData.questions_types_map[ question.question_type ],
			} )
		),
	moveDown: ( node, index, question ) =>
		dispatch(
			moveDown( {
				...node,
				index,
				parentID: question.ID,
				answerType:
					LearnDashData.questions_types_map[ question.question_type ],
			} )
		),
	removeAnswerEntity: ( answer, parent ) =>
		dispatch( removeAnswerEntity( answer, parent ) ),
	updateQuestionAnswers: ( question, answers ) =>
		dispatch( updateQuestionAnswers( question, answers ) ),
	updateQuestionType: ( node ) => dispatch( updateQuestionType( node ) ),
} );

export default connect(
	mapStateToProps,
	mapDispatchToProps
)( QuestionChildWorkspace );
